import { useEffect, useState } from 'react';
import { useLocation, useNavigationType, type NavigationType } from 'react-router';

// The state of the Back and Forward buttons in the staff toolbar.
//
// The data router of React Router writes `{ usr, key, idx }` into
// `window.history.state` for each entry. `idx` is the position of the entry in
// the history stack of this tab, and the first entry of the application has
// `idx` 0. The browser does not tell the page how many entries are after the
// current entry, so this module keeps the highest `idx` that the tab can go
// forward to. A PUSH removes all forward entries, so after a PUSH the current
// entry is the last entry. A POP or a REPLACE keeps the forward entries.

export interface HistoryNavState {
    /** The position of the current entry. */
    idx: number;
    /** The position of the last entry that Forward can reach. */
    maxIdx: number;
}

export interface HistoryNavButtons {
    canGoBack: boolean;
    canGoForward: boolean;
}

/** Gives the new state after one navigation. */
export function nextHistoryNavState(
    prev: HistoryNavState,
    idx: number,
    navigationType: NavigationType | `${NavigationType}`,
): HistoryNavState {
    if (navigationType === 'PUSH') return { idx, maxIdx: idx };
    return { idx, maxIdx: Math.max(prev.maxIdx, idx) };
}

/**
 * Gives the state when the document loads. A plain load of a URL removes all
 * forward entries, so the stored value applies only after a reload or after a
 * Back or Forward step into this document.
 */
export function loadHistoryNavState(
    idx: number,
    storedMaxIdx: number | null,
    loadType: string | undefined,
): HistoryNavState {
    const keepsForward = loadType === 'reload' || loadType === 'back_forward';
    const maxIdx = keepsForward && storedMaxIdx !== null ? Math.max(idx, storedMaxIdx) : idx;
    return { idx, maxIdx };
}

/** Gives the enabled state of the two buttons. */
export function historyNavButtons(state: HistoryNavState): HistoryNavButtons {
    return { canGoBack: state.idx > 0, canGoForward: state.idx < state.maxIdx };
}

/** Reads `idx` from a value of `window.history.state`. It gives 0 when it has no number. */
export function historyIdx(historyState: unknown): number {
    if (historyState && typeof historyState === 'object' && 'idx' in historyState) {
        const idx = (historyState as { idx: unknown }).idx;
        if (typeof idx === 'number' && Number.isInteger(idx) && idx >= 0) return idx;
    }
    return 0;
}

// The session storage keeps the highest index when the person reloads the
// page. The storage can be absent or can throw (a private window), so each
// access is in a try block. Without the storage, Forward is off after a reload.
const STORAGE_KEY = 'septima.history-nav.max-idx';

function readStoredMax(): number | null {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        const value = raw === null ? NaN : Number(raw);
        return Number.isInteger(value) && value >= 0 ? value : null;
    } catch {
        return null;
    }
}

function writeStoredMax(value: number): void {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, String(value));
    } catch {
        // The storage is not available. The state in memory is sufficient.
    }
}

function documentLoadType(): string | undefined {
    try {
        const entry = window.performance.getEntriesByType('navigation')[0];
        return entry && 'type' in entry ? String(entry.type) : undefined;
    } catch {
        return undefined;
    }
}

function initialState(): HistoryNavState {
    if (typeof window === 'undefined') return { idx: 0, maxIdx: 0 };
    return loadHistoryNavState(
        historyIdx(window.history.state),
        readStoredMax(),
        documentLoadType(),
    );
}

/** Tracks the history stack of the tab, and gives the state of the two buttons. */
export function useHistoryNavButtons(): HistoryNavButtons {
    const location = useLocation();
    const navigationType = useNavigationType();
    const [state, setState] = useState<HistoryNavState>(initialState);

    useEffect(() => {
        const idx = historyIdx(window.history.state);
        setState((prev) => {
            const next = nextHistoryNavState(prev, idx, navigationType);
            writeStoredMax(next.maxIdx);
            return next.idx === prev.idx && next.maxIdx === prev.maxIdx ? prev : next;
        });
        // `location.key` changes for each navigation, also to the same path.
    }, [location.key, navigationType]);

    return historyNavButtons(state);
}
